import { V as VividElement, a as attr, o as observable, v as volatile, h as html, d as createRegisterFunction, f as defineVividComponent } from './vivid-element.js';
import { _ as _curry1, a as _curry2, b as _has } from './_has.js';
import { c as classNames } from './class-names.js';
import { w as when } from './when.js';

const styles = ":host{display:inline-block;vertical-align:sub}.control.connotation-accent{--_connotation-color-primary: var(--vvd-icon-accent-primary, var(--vvd-color-canvas-text))}.control.connotation-announcement{--_connotation-color-primary: var(--vvd-icon-announcement-primary, var(--vvd-color-announcement-500))}.control.connotation-cta{--_connotation-color-primary: var(--vvd-icon-cta-primary, var(--vvd-color-cta-500))}.control.connotation-success{--_connotation-color-primary: var(--vvd-icon-success-primary, var(--vvd-color-success-500))}.control.connotation-warning{--_connotation-color-primary: var(--vvd-icon-warning-primary, var(--vvd-color-warning-300))}.control.connotation-alert{--_connotation-color-primary: var(--vvd-icon-alert-primary, var(--vvd-color-alert-500))}.control.connotation-information{--_connotation-color-primary: var(--vvd-icon-information-primary, var(--vvd-color-information-500))}.control.size--6{--_icon-block-size: calc(1px*(40 + 4*clamp(-1, var(--vvd-size-density, 0), 2)) - (1px*(24 + 4*clamp(-1, var(--vvd-size-density, 0), 2)))) }.control.size--5{--_icon-block-size: calc(1px*(20 + 4*clamp(-1, var(--vvd-size-density, 0), 2))) }.control.size--4{--_icon-block-size: calc(1px*(24 + 4*clamp(-1, var(--vvd-size-density, 0), 2))) }.control.size--3{--_icon-block-size: calc(1px*(28 + 4*clamp(-1, var(--vvd-size-density, 0), 2))) }.control.size--2{--_icon-block-size: calc(1px*(32 + 4*clamp(-1, var(--vvd-size-density, 0), 2))) }.control.size--1{--_icon-block-size: calc(1px*(36 + 4*clamp(-1, var(--vvd-size-density, 0), 2))) }.control.size-0{--_icon-block-size: calc(1px*(40 + 4*clamp(-1, var(--vvd-size-density, 0), 2))) }.control.size-1{--_icon-block-size: calc(1px*(44 + 4*clamp(-1, var(--vvd-size-density, 0), 2))) }.control.size-2{--_icon-block-size: calc(1px*(48 + 4*clamp(-1, var(--vvd-size-density, 0), 2))) }.control.size-3{--_icon-block-size: calc(1px*(52 + 4*clamp(-1, var(--vvd-size-density, 0), 2))) }.control.size-4{--_icon-block-size: calc(1px*(56 + 4*clamp(-1, var(--vvd-size-density, 0), 2))) }.control.size-5{--_icon-block-size: calc(1px*(60 + 4*clamp(-1, var(--vvd-size-density, 0), 2))) }.control:not(.size--6,.size--5,.size--4,.size--3,.size--2,.size--1,.size-0,.size-1,.size-2,.size-3,.size-4,.size-5){--_icon-block-size: 1em}.control{display:flex;margin:unset;block-size:var(--_icon-block-size);color:currentColor;contain:strict;inline-size:var(--_icon-block-size)}.control[class*=connotation]{color:var(--_connotation-color-primary)}slot,svg,::slotted(:where(svg,img)){margin:auto;block-size:inherit;inline-size:inherit}";

function _arity(n, fn) {
  /* eslint-disable no-unused-vars */
  switch (n) {
    case 0:
      return function () {
        return fn.apply(this, arguments);
      };

    case 1:
      return function (a0) {
        return fn.apply(this, arguments);
      };

    case 2:
      return function (a0, a1) {
        return fn.apply(this, arguments);
      };

    case 3:
      return function (a0, a1, a2) {
        return fn.apply(this, arguments);
      };

    case 4:
      return function (a0, a1, a2, a3) {
        return fn.apply(this, arguments);
      };

    case 5:
      return function (a0, a1, a2, a3, a4) {
        return fn.apply(this, arguments);
      };

    case 6:
      return function (a0, a1, a2, a3, a4, a5) {
        return fn.apply(this, arguments);
      };

    case 7:
      return function (a0, a1, a2, a3, a4, a5, a6) {
        return fn.apply(this, arguments);
      };

    case 8:
      return function (a0, a1, a2, a3, a4, a5, a6, a7) {
        return fn.apply(this, arguments);
      };

    case 9:
      return function (a0, a1, a2, a3, a4, a5, a6, a7, a8) {
        return fn.apply(this, arguments);
      };

    case 10:
      return function (a0, a1, a2, a3, a4, a5, a6, a7, a8, a9) {
        return fn.apply(this, arguments);
      };

    default:
      throw new Error('First argument to _arity must be a non-negative integer no greater than ten');
  }
}

function _identity(x) {
  return x;
}

/**
 * A function that does nothing but return the parameter supplied to it. Good
 * as a default or placeholder function.
 *
 * @func
 * @memberOf R
 * @since v0.1.0
 * @category Function
 * @sig a -> a
 * @param {*} x The value to return.
 * @return {*} The input value, `x`.
 * @example
 *
 *      R.identity(1); //=> 1
 *
 *      const obj = {};
 *      R.identity(obj) === obj; //=> true
 * @symb R.identity(a) = a
 */

var identity =
/*#__PURE__*/
_curry1(_identity);

/**
 * Creates a new function that, when invoked, caches the result of calling `fn`
 * for a given argument set and returns the result. Subsequent calls to the
 * memoized `fn` with the same argument set will not result in an additional
 * call to `fn`; instead, the cached result for that set of arguments will be
 * returned.
 *
 * Care must be taken when implementing key generation to avoid key collision,
 * or if tracking references, memory leaks and mutating arguments.
 *
 * @func
 * @memberOf R
 * @since v0.24.0
 * @category Function
 * @sig (*... -> String) -> (*... -> a) -> (*... -> a)
 * @param {Function} fn The function to generate the cache key.
 * @param {Function} fn The function to memoize.
 * @return {Function} Memoized version of `fn`.
 * @example
 *
 *      let count = 0;
 *      const factorial = R.memoizeWith(Number, n => {
 *        count += 1;
 *        return R.product(R.range(1, n + 1));
 *      });
 *      factorial(5); //=> 120
 *      factorial(5); //=> 120
 *      factorial(5); //=> 120
 *      count; //=> 1
 */

var memoizeWith =
/*#__PURE__*/
_curry2(function memoizeWith(mFn, fn) {
  var cache = {};
  return _arity(fn.length, function () {
    var key = mFn.apply(this, arguments);

    if (!_has(key, cache)) {
      cache[key] = fn.apply(this, arguments);
    }

    return cache[key];
  });
});

const ICONS_BASE_URL = "https://icon.resources.vonage.com";
const ICONS_VERSION = "4.6.4";

const numberConverter = {
  toView(value) {
    if (value === void 0) {
      return null;
    }
    return value.toString();
  },
  fromView(value) {
    if (typeof value === "string") {
      value = parseFloat(value.toString());
    }
    if (typeof value !== "number") {
      return void 0;
    }
    return isNaN(value) || !isFinite(value) ? void 0 : value;
  }
};

const PLACEHOLDER_ICON = `<svg width="80%" height="80%" viewBox="0 0 64 64">
    <g>
        <g stroke-width="6" stroke-linecap="round" fill="none">
            <path stroke="currentColor" d="M4,32 c0,15,12,28,28,28c8,0,16-4,21-9">
            </path>
            <path d="M60,32 C60,16,47.464,4,32,4S4,16,4,32">
            </path>
            <animateTransform values="0,32,32;360,32,32" attributeName="transform" type="rotate" repeatCount="indefinite" dur="750ms">
            </animateTransform>
        </g>
    </g>
</svg>`;

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
const PLACEHOLDER_DELAY = 500;
const PLACEHOLDER_TIMEOUT = 2e3;
const baseUrlTemplate = (resource, version) => [ICONS_BASE_URL, `v${version}`, resource].join("/");
const assertIsValidResponse = ({ ok, headers }) => {
  if (!ok || headers.get("content-type") !== "image/svg+xml") {
    throw new Error("Something went wrong");
  }
};
const extractSvg = (response) => {
  assertIsValidResponse(response);
  return response.text();
};
const loadSvg = (iconId, signal) => fetch(baseUrlTemplate([iconId, "svg"].join("."), ICONS_VERSION), {
  signal
}).then(extractSvg);
const resolveIcon = memoizeWith(
  identity,
  (iconId, signal) => iconId.trim() ? loadSvg(iconId, signal) : Promise.resolve("")
);
class Icon extends VividElement {
  constructor() {
    super(...arguments);
    this.iconLoaded = false;
    this.#abortController = null;
  }
  get iconUrl() {
    return !this.name ? this._svg : baseUrlTemplate(`${this.name}.svg`, ICONS_VERSION);
  }
  #abortController;
  async nameChanged() {
    if (this.#abortController) {
      this.#abortController.abort();
    }
    this.#abortController = new AbortController();
    this._svg = void 0;
    this.iconLoaded = false;
    let timeout = setTimeout(() => {
      this._svg = PLACEHOLDER_ICON;
      timeout = setTimeout(() => {
        if (this._svg === PLACEHOLDER_ICON) {
          this._svg = void 0;
        }
      }, PLACEHOLDER_TIMEOUT);
    }, PLACEHOLDER_DELAY);
    await resolveIcon(this.name ? this.name : "", this.#abortController.signal).then((svg) => {
      this._svg = svg;
    }).catch(() => {
      this._svg = void 0;
    }).finally(() => {
      clearTimeout(timeout);
      this.iconLoaded = true;
    });
  }
}
__decorateClass([
  attr
], Icon.prototype, "connotation", 2);
__decorateClass([
  attr({ converter: numberConverter })
], Icon.prototype, "size", 2);
__decorateClass([
  observable
], Icon.prototype, "_svg", 2);
__decorateClass([
  observable
], Icon.prototype, "iconLoaded", 2);
__decorateClass([
  attr
], Icon.prototype, "name", 2);
__decorateClass([
  volatile
], Icon.prototype, "iconUrl", 1);

const getClasses = ({ connotation, size }) => classNames(
  "control",
  [`connotation-${connotation}`, Boolean(connotation)],
  [`size-${size}`, typeof size === "number"]
);
const iconTemplate = html`
	<figure class="${getClasses}" ?aria-busy="${(x) => !x.iconLoaded}">
		<slot>
			${when(
  (x) => !x.iconLoaded,
  html`<img alt="${(x) => x.name}" src="${(x) => x.iconUrl}" />`
)}
			${when(
  (x) => x.iconLoaded && x._svg,
  (x) => html`${x._svg}`
)}
		</slot>
	</figure>
`;

const iconDefinition = defineVividComponent("icon", Icon, iconTemplate, [], {
  styles
});
const registerIcon = createRegisterFunction(iconDefinition);

export { Icon as I, iconDefinition as i, memoizeWith as m, registerIcon as r };
